import React, { useEffect } from "react";
import styles from "./Popup.module.css";

interface YesNoPopupProps {
  width?: number;
  height?: number;
  onCancel: () => void;
  onExit: () => void;
  title?: string;
  description: string | React.ReactNode;
  confirmText: string;
  cancelText: string;
  notifyNative?: boolean; // 🎯 네이티브 통신 여부 (기본값: true)
}

const YesNoPopup: React.FC<YesNoPopupProps> = ({
  width = 1200,
  height = 700,
  onCancel,
  onExit,
  title,
  description,
  confirmText,
  cancelText,
  notifyNative = false, // 🎯 기본값은 flase 설정
}) => {
  // 🎯 모달 표시/숨김 시 네이티브에 상태 전달 (notifyNative가 true일 때만)
  // 🎯 일반적으로 네이티브 통신이 필요없으니 건너뜀.
  useEffect(() => {
    if (!notifyNative) return;

    const notifyNativeModalState = (isVisible: boolean) => {
      try {
        if ((window as any).AndroidInterface) {
          (window as any).AndroidInterface.setModalState(isVisible);
          console.log(
            `📱 [YesNoPopup] 네이티브에 모달 상태 전달: ${
              isVisible ? "표시" : "숨김"
            }`
          );
        }
      } catch (error) {
        console.warn("📱 [YesNoPopup] 네이티브 브릿지 호출 실패:", error);
      }
    };

    // 모달 표시 시 네이티브에 알림
    notifyNativeModalState(true);

    // 컴포넌트 언마운트 시 모달 숨김 알림
    return () => {
      notifyNativeModalState(false);
    };
  }, [notifyNative]);

  // ESC 키로 닫기 (onExit 호출)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleExit();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // 🎯 모달 닫기 핸들러 (네이티브 상태 정리 포함)
  const handleCancel = () => {
    if (notifyNative) {
      // 🎯 네이티브 통신이 필요할 때만 호출
      try {
        if ((window as any).AndroidInterface) {
          (window as any).AndroidInterface.setModalState(false);
        }
      } catch (error) {
        console.warn("📱 [YesNoPopup] 모달 닫기 시 브릿지 호출 실패:", error);
      }
    }
    onCancel();
  };

  const handleExit = () => {
    if (notifyNative) {
      // 🎯 네이티브 통신이 필요할 때만 호출
      try {
        if ((window as any).AndroidInterface) {
          (window as any).AndroidInterface.setModalState(false);
        }
      } catch (error) {
        console.warn("📱 [YesNoPopup] 모달 닫기 시 브릿지 호출 실패:", error);
      }
    }
    onExit();
  };

  // dimmed 클릭 시 닫기 (onExit 호출)
  const handleDimmedClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleCancel();
  };

  // 모달 내부 클릭은 닫히지 않게
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className={styles.popupBg} onClick={handleDimmedClick}>
      <div className={styles.popupBox} onClick={handleModalClick}>
        <div className={styles.textBox}>
          <p className={styles.title}>{title}</p>
          <p className={styles.description}>{description}</p>
        </div>
        <div className={styles.buttonRow}>
          <button className={`${styles.buttonExit} ${styles.btn}`} onClick={handleExit}>
            {confirmText}
          </button>
          <button className={`${styles.buttonCancel} ${styles.btn}`} onClick={handleCancel}>
            {cancelText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default YesNoPopup;
